import { Preferences } from "@capacitor/preferences";
import i18n from "i18next";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { Country } from "./Regions";

export enum UnitSystem {
  Metric = "Metric",
  Imperial = "Imperial",
}

export enum CholesterolUnit {
  mgdl = "mgdl",
  mmol = "mmol",
}

export enum Gender {
  Male = "Male",
  Female = "Female",
}

export enum Language {
  Spanish = "es",
  English = "en",
  Portuguese = "pr",
}

interface ISettings {
  Country: Country;
  UnitSystem: UnitSystem;
  CholesterolUnit: CholesterolUnit;
}

type SettingsContextType = {
  settings: ISettings;
  updateSetting: (key: keyof ISettings, value: any) => void;
};

const SettingsContext = React.createContext<SettingsContextType | null>(null);

const DEFAULTS: ISettings = {
  Country: Country.Argentina,
  UnitSystem: UnitSystem.Metric,
  CholesterolUnit: CholesterolUnit.mgdl,
};

// Helpers para Preferences (guarda strings)
async function prefGet<T>(key: string): Promise<T | null> {
  const { value } = await Preferences.get({ key });
  if (value == null) return null;
  try {
    return JSON.parse(value) as T;
  } catch {
    // por si guardaron string plano alguna vez
    return value as unknown as T;
  }
}

async function prefSet(key: string, value: any): Promise<void> {
  await Preferences.set({ key, value: JSON.stringify(value) });
}

export const SettingsProvider = ({ children }: any) => {
  const [settings, setSettings] = useState<ISettings>(DEFAULTS);

  // Load inicial desde Preferences
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const storedCountry = await prefGet<Country>("country");
        const storedUnitSystem = await prefGet<UnitSystem>("unitSystem");
        const storedCholesterolUnit = await prefGet<CholesterolUnit>("cholesterolUnit");

        if (!mounted) return;

        setSettings({
          Country: storedCountry ?? DEFAULTS.Country,
          UnitSystem: storedUnitSystem ?? DEFAULTS.UnitSystem,
          CholesterolUnit: storedCholesterolUnit ?? DEFAULTS.CholesterolUnit,
        });
      } catch (e) {
        console.warn("[Settings] Preferences not available, using defaults.", e);
        if (mounted) setSettings(DEFAULTS);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  const updateSetting = useCallback(
    (key: keyof ISettings, value: any) => {
      const newSettings = { ...settings, [key]: value } as ISettings;

      // Persist (sin reventar si algo falla)
      (async () => {
        try {
          await prefSet("country", newSettings.Country);
          await prefSet("unitSystem", newSettings.UnitSystem);
          await prefSet("cholesterolUnit", newSettings.CholesterolUnit);
        } catch (e) {
          console.warn("[Settings] Could not persist settings.", e);
        }
      })();

      setSettings(newSettings);
    },
    [settings]
  );

  return (
    <SettingsContext.Provider value={{ settings, updateSetting }}>
      {children}
    </SettingsContext.Provider>
  );
};

export function useUnitSystem(): [UnitSystem, (value: UnitSystem) => void] {
  const ctx = useContext(SettingsContext);
  const setUnitSystem = (unit: UnitSystem) => ctx?.updateSetting("UnitSystem", unit);
  return [ctx?.settings.UnitSystem as UnitSystem, setUnitSystem];
}

export function useCholesterolUnit(): [CholesterolUnit, (value: CholesterolUnit) => void] {
  const ctx = useContext(SettingsContext);
  const setCholesterolUnit = (unit: CholesterolUnit) => ctx?.updateSetting("CholesterolUnit", unit);
  return [ctx?.settings.CholesterolUnit as CholesterolUnit, setCholesterolUnit];
}

export function useLanguage(): [Language, (value: Language) => Promise<void>] {
  const language = i18n.language.substr(0, 2) as Language;

  async function setLanguage(value: Language) {
    await i18n.changeLanguage(value);
  }

  return [language, setLanguage];
}

export function useCountry(): [Country, (value: Country) => void] {
  const ctx = useContext(SettingsContext);
  const setCountry = (country: Country) => ctx?.updateSetting("Country", country);
  return [ctx?.settings.Country as Country, setCountry];
}
